import { Link } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import HotListCard from "@/components/feature/HotListCard";
import { topics } from "@/mocks/topics";
import { hotKeywords } from "@/mocks/site";
import type { NewsItem } from "@/types/content";

interface NewsBoardProps {
  active: string;
  onSelect: (slug: string) => void;
  list: NewsItem[];
}

export default function NewsBoard({ active, onSelect, list }: NewsBoardProps) {
  const current = topics.find((topic) => topic.slug === active);

  return (
    <section id="board" className="w-full scroll-mt-20 bg-background-100">
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              {current ? `${current.name} · 爆料列表` : "全部爆料列表"}
            </h2>
            <p className="mt-2 text-sm leading-7 text-foreground-600">
              {current ? current.desc : "默认按发布时间排列，最新的内容排在前面。"}
            </p>
          </div>
          <span className="text-xs text-foreground-500">当前共 {list.length} 条内容</span>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onSelect("all")}
            className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
              active === "all"
                ? "bg-primary-500 text-background-50 font-medium"
                : "border border-background-300/80 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            全部
          </button>
          {topics.map((topic) => (
            <button
              key={topic.slug}
              type="button"
              onClick={() => onSelect(topic.slug)}
              className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                active === topic.slug
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "border border-background-300/80 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {topic.name}
            </button>
          ))}
        </div>

        <div className="mt-7 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:gap-8">
          <div>
            {list.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {list.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-400/70 bg-background-50 py-16 text-center">
                <p className="text-sm text-foreground-600">该栏目暂时没有新的内容，稍后再来看看。</p>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <HotListCard limit={6} />

            <div className="rounded-lg border border-background-300/60 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                </span>
                热词榜
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {hotKeywords.map((word) => (
                  <span
                    key={word}
                    className="rounded-full border border-background-300/80 bg-background-100 px-3 py-1.5 text-xs text-foreground-700"
                  >
                    #{word}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h3 className="font-heading text-base font-bold text-secondary-900">想补充线索？</h3>
              <p className="mt-2 text-sm leading-6 text-secondary-900">
                如果你掌握更完整的现场信息，欢迎通过关于我们页面的表单提交，我们会逐条核对。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                前往提交
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}